import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Clock, Users, CheckCircle, AlertCircle, MessageSquare, BarChart3, FileText, Plus, Send, ThumbsUp, ThumbsDown, User, Settings, LogOut, ChevronRight, ChevronDown, ChevronUp, X, Mic, MicOff, Mail, Search, RefreshCw, Trash2, XCircle, ArrowLeft, Loader2, Bell, BellOff, Eye, Download, Filter, Columns, ArrowUpDown, Maximize2, Minimize2, ChevronLeft, ShieldAlert, Heart, Phone } from 'lucide-react';
import { useToast, useCrisisAlert, ToastProvider, CrisisContext } from '../shared.jsx';
import { storage, addAuditEntry, calculateWorkingHours, OWL_LOGO, EMAIL_CONFIG, detectHarmContent, notifyMentorOfHarmDetection, sendNotification, VoiceInputButton, useVoiceInput, initFCM, removeFCMToken, notificationPermissionGranted, requestNotificationPermission, CrisisResourceModal, getUrlParams, clearUrlParams, initFirebase } from '../shared.jsx';


function FollowUpStep({ conflict, user, canEdit, onUpdate, onReturnToOverview, onLogout }) {
  const toast = useToast();
  const [ratings, setRatings] = useState({
    processSatisfaction: 0,
    feltHeard: 0,
    partnerHeard: 0
  });
  const [hasRated, setHasRated] = useState(false);

  // Check if user has already rated
  const followUp = conflict.steps?.followUp;
  const userRatings = followUp?.ratings?.[user.id];

  React.useEffect(() => {
    if (userRatings) {
      setRatings(userRatings);
      setHasRated(true);
    }
  }, [userRatings]);

  const handleRatingChange = (category, value) => {
    setRatings({ ...ratings, [category]: parseInt(value) });
  };

  const handleSubmitRatings = async () => {
    // Validate all ratings are set
    if (ratings.processSatisfaction === 0 || ratings.feltHeard === 0 || ratings.partnerHeard === 0) {
      toast('Please provide all three ratings before submitting');
      return;
    }

    try {
      const updated = {
        ...conflict,
        steps: {
          ...conflict.steps,
          followUp: {
            ...conflict.steps.followUp,
            ratings: {
              ...(conflict.steps.followUp?.ratings || {}),
              [user.id]: {
                ...ratings,
                userName: user.name,
                userEmail: user.email,
                timestamp: new Date().toISOString()
              }
            }
          }
        },
        updatedAt: new Date().toISOString(),
        updatedBy: user.id
      };

      // Check if all mentees have rated
      const allRatings = updated.steps.followUp.ratings;
      const ratingCount = Object.keys(allRatings).length;
      const totalMentees = conflict.mentees?.length || 1;

      if (ratingCount >= totalMentees) {
        updated.steps.followUp.completed = true;
        updated.status = 'resolved';
        updated.statusHistory.push({
          status: 'resolved',
          timestamp: new Date().toISOString()
        });
      }

      addAuditEntry(updated, user.id, user.name, 'Submitted follow-up ratings', null, user.email);
      await storage.set(`conflict:${conflict.id}`, updated, true);
      setHasRated(true);
      await onUpdate();
      toast('Your ratings have been submitted!');
      if (onReturnToOverview) onReturnToOverview();
    } catch (error) {
      console.error('Error submitting ratings:', error);
      toast('Failed to submit ratings. Please try again.');
    }
  };

  // Calculate statistics
  const allRatings = followUp?.ratings || {};
  const allComplete = followUp?.completed;
  const ratingValues = Object.values(allRatings);

  const calculateAverage = (category) => {
    if (ratingValues.length === 0) return 0;
    const sum = ratingValues.reduce((acc, r) => acc + (r[category] || 0), 0);
    return (sum / ratingValues.length).toFixed(1);
  };

  const calculateAgreement = (category) => {
    if (ratingValues.length < 2) return 100;
    const values = ratingValues.map(r => r[category]);
    const avg = values.reduce((a, b) => a + b, 0) / values.length;
    const variance = values.reduce((acc, val) => acc + Math.pow(val - avg, 2), 0) / values.length;
    const stdDev = Math.sqrt(variance);
    // Convert to percentage (lower std dev = higher agreement)
    const agreement = Math.max(0, 100 - (stdDev * 25));
    return Math.round(agreement);
  };

  // Calculate time spent
  const calculateTimeSpent = () => {
    const resolved = conflict.statusHistory?.find(h => h.status === 'resolved');
    if (!resolved) return null;
    
    const hours = calculateWorkingHours(conflict.createdAt, resolved.timestamp, conflict.responseHours);
    const days = Math.floor(hours / 24);
    const remainingHours = Math.floor(hours % 24);
    return { hours: hours.toFixed(1), days, remainingHours };
  };

  const timeSpent = calculateTimeSpent();

  // Count steps taken
  const stepsTaken = conflict.statusHistory?.length || 0;

  // Calculate maximum possible time given the constraints
  const calculateMaxPossibleTime = () => {
    const maxCommunicateCycles = 5;
    const maxAlternativesPerPerson = 3;
    const numMentees = conflict.mentees?.length || 1;
    
    // Communicate phase: cooling period + response window per cycle (both directions)
    // Each cycle: statement + paraphrase = 2 responses
    // Max cycles = 5, so 10 total responses (5 statements, 5 paraphrases)
    const communicateTime = maxCommunicateCycles * 2 * (conflict.responseWaitTime + conflict.responseWindow);
    
    // Explore Alternatives: responseWindow per alternative
    const alternativesTime = maxAlternativesPerPerson * conflict.responseWindow * numMentees;
    
    // Total max time in minutes
    const totalMinutes = communicateTime + alternativesTime;
    const totalHours = totalMinutes / 60;
    
    return {
      minutes: totalMinutes,
      hours: totalHours.toFixed(1),
      days: Math.floor(totalHours / 24),
      remainingHours: Math.floor(totalHours % 24)
    };
  };

  const maxPossibleTime = calculateMaxPossibleTime();
  
  // Calculate efficiency percentage
  const calculateEfficiency = () => {
    if (!timeSpent || !maxPossibleTime) return 0;
    const actualHours = parseFloat(timeSpent.hours);
    const maxHours = parseFloat(maxPossibleTime.hours);
    const efficiency = ((maxHours - actualHours) / maxHours) * 100;
    return Math.max(0, Math.min(100, Math.round(efficiency)));
  };

  const efficiency = calculateEfficiency();

  const ratingCategories = [
    {
      key: 'processSatisfaction',
      label: 'Overall Process Satisfaction',
      description: 'How satisfied are you with the conflict resolution process?',
      lowLabel: 'Very Dissatisfied',
      highLabel: 'Very Satisfied'
    },
    {
      key: 'feltHeard',
      label: 'Felt Heard and Understood',
      description: 'How much did you feel heard and understood during this process?',
      lowLabel: 'Not at all',
      highLabel: 'Completely'
    },
    {
      key: 'partnerHeard',
      label: 'Partner Felt Heard',
      description: 'How much do you think your conflict partner(s) felt heard and understood?',
      lowLabel: 'Not at all',
      highLabel: 'Completely'
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h4 className="font-medium text-stone-800 mb-3">Follow-up & Reflection</h4>
        <p className="text-sm text-stone-600 mb-4">
          Reflect on the conflict resolution process by providing ratings. Your feedback helps improve future conflict resolutions.
        </p>
      </div>

      {/* Ratings Form */}
      {!hasRated && canEdit ? (
        <div className="space-y-6">
          {ratingCategories.map((category) => (
            <div key={category.key} className="bg-white rounded-lg p-6 border-2 border-stone-200">
              <div className="mb-4">
                <h5 className="font-medium text-stone-800 mb-1">{category.label}</h5>
                <p className="text-sm text-stone-600">{category.description}</p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between text-xs text-stone-500 px-1">
                  <span>{category.lowLabel}</span>
                  <span>{category.highLabel}</span>
                </div>
                <div className="flex gap-2 justify-center">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <label
                      key={rating}
                      className={`cursor-pointer transition-all ${
                        ratings[category.key] === rating
                          ? 'scale-110'
                          : 'opacity-60 hover:opacity-100'
                      }`}
                    >
                      <input
                        type="radio"
                        name={`rating-${category.key}`}
                        value={rating}
                        checked={ratings[category.key] === rating}
                        onChange={(e) => handleRatingChange(category.key, e.target.value)}
                        className="sr-only"
                      />
                      <div
                        className={`w-14 h-14 rounded-full flex items-center justify-center font-medium text-lg transition-all ${
                          ratings[category.key] === rating
                            ? 'bg-amber-700 text-white shadow-lg'
                            : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                        }`}
                      >
                        {rating}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          ))}

          <button
            onClick={handleSubmitRatings}
            disabled={ratings.processSatisfaction === 0 || ratings.feltHeard === 0 || ratings.partnerHeard === 0}
            className="w-full bg-amber-700 hover:bg-amber-800 disabled:bg-stone-300 disabled:cursor-not-allowed text-white py-4 rounded-lg font-medium transition-all transform hover:scale-[1.02] active:scale-[0.98]"
          >
            Submit Ratings
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          <div className={`p-6 rounded-lg border ${hasRated ? 'bg-blue-50 border-blue-200' : 'bg-stone-50 border-stone-200'}`}>
            <div className={`flex items-center gap-2 font-medium mb-2 ${hasRated ? 'text-blue-800' : 'text-stone-600'}`}>
              <CheckCircle className="w-5 h-5" />
              {hasRated ? 'Your ratings have been submitted' : 'This conflict has been resolved'}
            </div>
            <p className={`text-sm ${hasRated ? 'text-blue-600' : 'text-stone-500'}`}>
              {hasRated 
                ? (allComplete 
                    ? 'All participants have completed their ratings.' 
                    : 'Waiting for other participants to submit their ratings...')
                : 'Ratings are no longer being accepted for this conflict.'}
            </p>
          </div>

          {/* Show user's ratings if they submitted them */}
          {hasRated && (
            <div>
              <h5 className="font-medium text-stone-700 mb-3">Your Ratings:</h5>
              <div className="grid grid-cols-3 gap-4">
                {ratingCategories.map((category) => (
                  <div key={category.key} className="p-4 bg-white rounded-lg border border-stone-200">
                    <div className="text-sm text-stone-600 mb-2">{category.label}</div>
                    <div className="text-2xl font-medium text-amber-700">{ratings[category.key]}/5</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Summary Section - Only show when all have rated */}
      {allComplete && (
        <div className="mt-8 space-y-6">
          <div className="border-t border-stone-200 pt-6">
            <h4 className="text-xl font-medium text-stone-800 mb-4">Conflict Resolution Summary</h4>
          </div>

          {/* Time and Steps */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-6 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-3 mb-2">
                <Clock className="w-6 h-6 text-blue-600" />
                <h5 className="font-medium text-blue-800">Time to Resolution</h5>
              </div>
              {timeSpent && (
                <div>
                  <div className="text-3xl font-medium text-blue-900">{timeSpent.hours}h</div>
                  <div className="text-sm text-blue-700 mt-1">
                    ({timeSpent.days} days, {timeSpent.remainingHours} hours)
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border border-purple-200">
              <div className="flex items-center gap-3 mb-2">
                <BarChart3 className="w-6 h-6 text-purple-600" />
                <h5 className="font-medium text-purple-800">Steps Taken</h5>
              </div>
              <div className="text-3xl font-medium text-purple-900">{stepsTaken}</div>
              <div className="text-sm text-purple-700 mt-1">status changes</div>
            </div>
          </div>

          {/* Average Ratings */}
          <div>
            <h5 className="font-medium text-stone-800 mb-3">Average Ratings (All Participants)</h5>
            <div className="grid grid-cols-3 gap-4">
              {ratingCategories.map((category) => (
                <div key={category.key} className="p-5 bg-white rounded-lg border-2 border-stone-200">
                  <div className="text-sm text-stone-600 mb-3">{category.label}</div>
                  <div className="flex items-end gap-2 mb-2">
                    <div className="text-3xl font-medium text-amber-700">
                      {calculateAverage(category.key)}
                    </div>
                    <div className="text-stone-500 mb-1">/5.0</div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-stone-200">
                    <div className="text-xs text-stone-500 mb-1">Agreement Level</div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-stone-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-green-500 transition-all"
                          style={{ width: `${calculateAgreement(category.key)}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-stone-700">
                        {calculateAgreement(category.key)}%
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Comparative Statement */}
          <div className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-2 border-green-200">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h5 className="font-medium text-green-800 text-lg mb-3">Resolution Achievement</h5>
                
                <div className="mb-4">
                  <p className="text-green-700 mb-2">
                    This conflict was resolved in <strong>{timeSpent?.hours || 0} hours</strong> across <strong>{stepsTaken} steps</strong>.
                    {timeSpent && timeSpent.hours < 24 ? (
                      <span> This represents an efficient resolution completed in less than one day.</span>
                    ) : timeSpent && timeSpent.hours < 72 ? (
                      <span> This represents a moderately paced resolution.</span>
                    ) : (
                      <span> This represents a thorough resolution process that took multiple days.</span>
                    )}
                  </p>
                  
                  <div className="p-4 bg-white rounded-lg border border-green-300 mt-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-green-800">Time Efficiency</span>
                      <span className="text-lg font-bold text-green-700">{efficiency}%</span>
                    </div>
                    <div className="mb-3">
                      <div className="h-3 bg-green-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-green-500 to-emerald-600 transition-all"
                          style={{ width: `${efficiency}%` }}
                        />
                      </div>
                    </div>
                    <p className="text-xs text-green-700">
                      <strong>Excellent work!</strong> Given the maximum wait times and response windows, this conflict could have taken up to{' '}
                      <strong>{maxPossibleTime.hours} hours</strong> ({maxPossibleTime.days > 0 ? `${maxPossibleTime.days} day${maxPossibleTime.days !== 1 ? 's' : ''} and ` : ''}{maxPossibleTime.remainingHours} hours).{' '}
                      You resolved it {efficiency}% faster by responding promptly and engaging constructively.
                    </p>
                  </div>
                </div>
                
                <p className="text-green-700">
                  The average satisfaction rating of <strong>{calculateAverage('processSatisfaction')}/5.0</strong> indicates{' '}
                  {calculateAverage('processSatisfaction') >= 4 ? 'high' : calculateAverage('processSatisfaction') >= 3 ? 'moderate' : 'lower'} satisfaction with the process.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Logout link */}
      {onLogout && (
        <div className="pt-4 border-t border-stone-200 text-center">
          <button
            onClick={onLogout}
            className="text-sm text-stone-500 hover:text-amber-700 transition-colors"
          >
            Done for now? Log out →
          </button>
        </div>
      )}
    </div>
  );
}

// Agree & Implement Step

export default FollowUpStep;
